"use client";

/**
 * LiquidVerticalGroupExpansible
 * ──────────────────────────────────────────────────────────────────────
 * Variante de `LiquidPillGroupVertical` con la MISMA estética visual
 * (burbuja líquida, bounce, deformación, hover, colores), pero con la
 * capacidad de que cada item expanda contenido propio debajo de su pill
 * cuando es activado (click).
 *
 * Diferencias respecto a LiquidPillGroupVertical:
 *   - Cada item puede tener `content?: ReactNode` que se revela al estar
 *     activo.
 *   - Cuando un item está activo, los demás se desvanecen (fade + collapse
 *     + blur + scale) en cascada con stagger, dejando solo el activo
 *     visible junto a su contenido expandido.
 *   - El click sobre el item activo lo colapsa (toggle).
 *
 * El componente es controlable (`activeValue` / `onChange`) o no
 * controlable (gestiona su propio estado).
 *
 * Es un primitive genérico — no contiene lógica de chat ni de ningún
 * dominio. El consumidor decide qué renderear en `content`.
 */

import * as React from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";
import { LiquidPillExpansible } from "./liquid-pill-expansible";
import type { LiquidPillSize } from "./liquid-pill";
import {
  LiquidSurface,
  groupSquishStyle,
  shouldGroupSquish,
  type LiquidDeformAxis,
  type LiquidSurfaceBounceIntensity,
} from "./liquid-surface";

export interface LiquidVerticalGroupExpansibleItem {
  value: string;
  label?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: CircleActionVariant;
  disabled?: boolean;
  trailing?: React.ReactNode;
  /** Contenido que se revela cuando este item está activo. */
  content?: React.ReactNode;
  /**
   * Override de size visual de la pill colapsada para este item específico.
   * Útil para casos como un CTA inline ("Nuevo chat") que debe ser más
   * chico que los items estándar. Por default hereda el size del grupo.
   */
  size?: LiquidPillSize;
}

export interface LiquidVerticalGroupExpansibleProps {
  items: LiquidVerticalGroupExpansibleItem[];
  /** Modo controlado: id del item activo. `null` significa ninguno. */
  activeValue?: string | null;
  /** Modo no controlado: id inicial activo. */
  defaultActiveValue?: string | null;
  onChange?: (value: string | null) => void;
  onHover?: (value: string | null) => void;
  size?: "xxs" | "xs" | "sm" | "md";
  disableBounce?: boolean;
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  deformAxis?: LiquidDeformAxis;
  /** Altura máxima animada del contenido expandido (px). Default `620`. */
  contentMaxHeight?: number;
  /** Altura máxima animada de cada celda en estado normal (px). Default `96`. */
  cellMaxHeight?: number;
  /** Altura máxima animada de la celda activa con contenido (px). Default `760`. */
  activeCellMaxHeight?: number;
  /** Renderiza cada item con su propia superficie tipo LiquidPill. */
  individualPills?: boolean;
  className?: never;
}

type BubbleRect = { top: number; left: number; width: number; height: number };

const SLIDE_MS = 560;
const SLIDE_EASE = "cubic-bezier(0.22,1,0.36,1)";

export function LiquidVerticalGroupExpansible({
  items,
  activeValue: controlledActive,
  defaultActiveValue = null,
  onChange,
  onHover,
  size = "sm",
  disableBounce = false,
  bounceIntensity = "strong",
  deformAxis = "vertical",
  contentMaxHeight = 620,
  cellMaxHeight = 96,
  activeCellMaxHeight = 760,
  individualPills = false,
}: LiquidVerticalGroupExpansibleProps) {
  /* ── Estado controlado / no controlado ──────────────────────────── */
  const isControlled = controlledActive !== undefined;
  const [uncontrolledActive, setUncontrolledActive] = useState<string | null>(
    defaultActiveValue,
  );
  const activeValue = isControlled ? controlledActive : uncontrolledActive;

  const setActiveValue = useCallback(
    (next: string | null) => {
      if (!isControlled) setUncontrolledActive(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );

  /* ── Refs ───────────────────────────────────────────────────────── */
  const containerRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const cellRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevCellRects = useRef<Map<string, DOMRect>>(new Map());

  /* ── Burbuja líquida ────────────────────────────────────────────── */
  const [renderBubble, setRenderBubble] = useState<BubbleRect | null>(null);
  const [animate, setAnimate] = useState(false);
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);

  const prevBubbleRect = useRef<BubbleRect | null>(null);
  const prevDisplayTarget = useRef<string | null>(null);

  const displayActiveValue = activeValue;
  const hasActive = activeValue != null;
  const activeItem = items.find((i) => i.value === displayActiveValue);
  const activeHasContent =
    !!activeItem && activeItem.content != null && (activeItem.content as unknown) !== false;
  // Cuando un item está activo con contenido expandido, ocultamos la
  // burbuja líquida: el header ya no necesita el fondo de la pill porque
  // pasa a ser el encabezado del contenido.
  const displayTarget = individualPills || activeHasContent
    ? null
    : hasActive
      ? displayActiveValue
      : (hoveredValue ?? displayActiveValue ?? null);
  const displayItem = items.find((i) => i.value === displayTarget);
  const displayVariant = resolveCircleActionVariant(displayItem?.variant ?? "slate");

  const triggerBounce = useCallback(() => {
    if (!shouldGroupSquish(bounceIntensity)) return;
    const el = bubbleRef.current;
    if (!el) return;
    el.style.animation = "none";
    void el.offsetHeight;
    el.style.animation =
      "lvge-vertical-bounce 560ms cubic-bezier(0.34,1.2,0.64,1) both";
  }, [bounceIntensity]);

  const measureAt = useCallback(
    (target: string) => {
      const idx = items.findIndex((i) => i.value === target);
      const el = itemRefs.current[idx];
      const container = containerRef.current;
      if (!el || !container) return null;
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      return {
        top: eRect.top - cRect.top,
        left: eRect.left - cRect.left,
        width: eRect.width,
        height: eRect.height,
      };
    },
    [items],
  );

  useLayoutEffect(() => {
    if (!displayTarget) {
      setRenderBubble(null);
      setAnimate(false);
      prevDisplayTarget.current = null;
      prevBubbleRect.current = null;
      return;
    }

    const pos = measureAt(displayTarget);
    if (!pos) return;

    const wasNull = prevDisplayTarget.current == null;
    const prevTarget = prevDisplayTarget.current;
    const moved = prevTarget != null && prevTarget !== displayTarget;
    const prevRect = prevBubbleRect.current;

    prevDisplayTarget.current = displayTarget;

    if (wasNull) {
      prevBubbleRect.current = pos;
      setRenderBubble(pos);
      setAnimate(false);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setAnimate(true)),
      );
      return;
    }

    prevBubbleRect.current = pos;
    setRenderBubble(pos);
    if (moved && animate && !disableBounce && prevRect) triggerBounce();
  }, [displayTarget, measureAt, animate, disableBounce, triggerBounce]);

  /* FLIP de celdas: asegura que la celda activa se vea arrastrándose a su
     nueva posición cuando los siblings colapsan. El reflow por max-height es
     suave en muchos casos, pero en CTA/último item dentro de scroll + overrides
     de altura puede snapear. Medimos before/after y animamos explícitamente el
     delta vertical de la celda activa. */
  useLayoutEffect(() => {
    const nextRects = new Map<string, DOMRect>();

    items.forEach((item, idx) => {
      const el = cellRefs.current[idx];
      if (!el) return;
      nextRects.set(item.value, el.getBoundingClientRect());
    });

    if (activeValue) {
      const activeIdx = items.findIndex((item) => item.value === activeValue);
      const activeEl = cellRefs.current[activeIdx];
      const prevRect = prevCellRects.current.get(activeValue);
      const nextRect = nextRects.get(activeValue);

      if (activeEl && prevRect && nextRect) {
        const deltaY = prevRect.top - nextRect.top;
        const deltaX = prevRect.left - nextRect.left;

        if (Math.abs(deltaY) > 1 || Math.abs(deltaX) > 1) {
          activeEl.animate(
            [
              { transform: `translate3d(${deltaX}px, ${deltaY}px, 0)` },
              { transform: "translate3d(0, 0, 0)" },
            ],
            {
              duration: 420,
              easing: "cubic-bezier(0.22, 1.02, 0.36, 1)",
              fill: "both",
            },
          );
        }
      }
    }

    prevCellRects.current = nextRects;
  }, [activeValue, items]);

  /* La burbuja también re-mide al cambiar el tamaño del contenedor
     (p.ej. cuando otras celdas colapsan al expandir una). */
  useEffect(() => {
    if (!displayTarget) return;
    const ro = new ResizeObserver(() => {
      const pos = measureAt(displayTarget);
      if (pos) {
        prevBubbleRect.current = pos;
        setRenderBubble(pos);
      }
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [displayTarget, measureAt]);

  /* ── Tamaños ────────────────────────────────────────────────────── */
  const gapClass =
    size === "xxs" ? "gap-px" : size === "xs" ? "gap-0.5" : "gap-1";
  const padClass =
    size === "xxs"
      ? "px-1.5 py-[3px]"
      : size === "xs"
        ? "px-2 py-1"
        : size === "sm"
          ? "px-2.5 py-1.5"
          : "px-2.5 py-1.5";
  const textClass =
    size === "xxs"
      ? "text-[9px]"
      : size === "xs"
        ? "text-[10px]"
        : size === "sm"
          ? "text-[11px]"
          : "text-[12px]";
  const itemGapClass = size === "xxs" ? "gap-1" : "gap-2";

  const slideTransition = disableBounce
    ? [
        `top ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `left ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `width ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `height ${SLIDE_MS}ms ${SLIDE_EASE}`,
        "opacity 200ms ease",
      ].join(",")
    : [
        `top ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `left ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `width ${SLIDE_MS}ms ${SLIDE_EASE}`,
        `height ${SLIDE_MS}ms ${SLIDE_EASE}`,
        "opacity 240ms ease",
      ].join(",");

  /* ── Handlers ───────────────────────────────────────────────────── */
  const handleItemClick = (item: LiquidVerticalGroupExpansibleItem) => {
    if (item.disabled) return;
    setActiveValue(activeValue === item.value ? null : item.value);
  };

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <div
      ref={containerRef}
      className={cn(
        "lvge-container relative flex flex-col items-start w-full",
        gapClass,
        "overflow-visible",
      )}
      onMouseLeave={() => {
        setHoveredValue(null);
        onHover?.(null);
      }}
    >
      <div
        ref={bubbleRef as React.Ref<HTMLDivElement>}
        aria-hidden
        className="lvge-bubble pointer-events-none absolute"
        style={{
          top: renderBubble?.top ?? 0,
          left: renderBubble?.left ?? 0,
          width: renderBubble?.width ?? 0,
          height: renderBubble?.height ?? 0,
          opacity: renderBubble ? 1 : 0,
          ...groupSquishStyle(bounceIntensity, deformAxis),
          transition: animate ? slideTransition : "opacity 240ms ease",
        }}
      >
        <LiquidSurface
          as="div"
          variant={displayVariant}
          tone="tinted"
          shape="pill"
          size="free"
          shadow="neutral"
          interactive={false}
          cursorGlow={false}
          reactive
          hoverDeform={false}
          hoverBounce={false}
          bounceIntensity={bounceIntensity}
          style={{ position: "absolute", inset: 0 }}
        />
      </div>

      {items.map((item, idx) => {
        const isActive = item.value === activeValue;
        const isHidden = hasActive && !isActive;
        const hasContent = item.content != null && item.content !== false;
        const variant = resolveCircleActionVariant(item.variant ?? "slate");
        const vars = circleActionVariantCssVars(variant);

        return (
          <div
            key={item.value}
            ref={(el) => {
              cellRefs.current[idx] = el;
            }}
            style={
              {
                ["--lvge-cell-index" as any]: idx,
                ["--lvge-cell-max" as any]: `${cellMaxHeight}px`,
                ["--lvge-cell-active-max" as any]: `${activeCellMaxHeight}px`,
                ["--lvge-cell-content-max" as any]: `${contentMaxHeight}px`,
              } as React.CSSProperties
            }
            className={cn(
              "lvge-cell",
              isHidden && "lvge-cell--hidden",
              isActive && "lvge-cell--active",
              hasContent && "lvge-cell--has-content",
              individualPills && "lvge-cell--individual-pill",
            )}
          >
            {individualPills ? (
              <LiquidPillExpansible
                variant={variant}
                active={isActive}
                disabled={item.disabled || isHidden}
                content={hasContent ? item.content : null}
                contentMaxHeight={contentMaxHeight}
                shape="pill"
                expandedShape="rounded"
                shadow="neutral"
                {...(item.size ? { size: item.size } : {})}
                bounceIntensity={bounceIntensity}
                deformAxis={deformAxis}
                className="lvge-cell-pill"
                onToggle={() => handleItemClick(item)}
                onHeaderMouseEnter={() => {
                  if (isHidden) return;
                  setHoveredValue(item.value);
                  onHover?.(item.value);
                }}
                header={
                  <span
                    ref={(el) => {
                      itemRefs.current[idx] = el as HTMLButtonElement | null;
                    }}
                    style={vars as React.CSSProperties}
                    className={cn(
                      "lvge-item relative z-[1] inline-flex w-full max-w-full items-center justify-between gap-2 rounded-full",
                      "select-none overflow-hidden font-medium tracking-[-0.01em] text-left",
                      "motion-safe:[transition:color_280ms_ease,transform_300ms_cubic-bezier(0.34,1.56,0.64,1)]",
                      padClass,
                      textClass,
                      "lvge-item--individual-pill",
                      isActive ? "lvge-item--active" : "lvge-item--idle",
                    )}
                  >
                    <span
                      className={cn(
                        "relative z-[2] inline-flex min-w-0 flex-1 items-center",
                        itemGapClass,
                      )}
                    >
                      {item.icon && (
                        <span className="flex-shrink-0">{item.icon}</span>
                      )}
                      {item.label != null && (
                        <span className="flex min-w-0 flex-1 items-center overflow-visible">
                          {item.label}
                        </span>
                      )}
                    </span>
                    {item.trailing != null && (
                      <span className="relative z-[2] flex-shrink-0">{item.trailing}</span>
                    )}
                  </span>
                }
              />
            ) : (
              <>
                <button
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  type="button"
                  disabled={item.disabled || isHidden}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={() => {
                    if (isHidden) return;
                    setHoveredValue(item.value);
                    onHover?.(item.value);
                  }}
                  style={vars as React.CSSProperties}
                  className={cn(
                    "lvge-item relative z-[1] inline-flex w-full max-w-full items-center justify-between gap-2 rounded-full",
                    "select-none overflow-hidden font-medium tracking-[-0.01em] text-left",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-1",
                    "disabled:pointer-events-none disabled:opacity-40",
                    "motion-safe:[transition:color_280ms_ease,transform_300ms_cubic-bezier(0.34,1.56,0.64,1)]",
                    padClass,
                    textClass,
                    isActive ? "lvge-item--active" : "lvge-item--idle",
                  )}
                >
                  <span
                    className={cn(
                      "relative z-[2] inline-flex min-w-0 flex-1 items-center",
                      itemGapClass,
                    )}
                  >
                    {item.icon && (
                      <span className="flex-shrink-0">{item.icon}</span>
                    )}
                    {item.label != null && (
                      <span className="flex min-w-0 flex-1 items-center truncate whitespace-nowrap">
                        {item.label}
                      </span>
                    )}
                  </span>
                  {item.trailing != null && (
                    <span className="relative z-[2] flex-shrink-0">{item.trailing}</span>
                  )}
                </button>

                {hasContent && (
                  <div className="lvge-cell__content" aria-hidden={!isActive}>
                    <div className="lvge-cell__content-inner">{item.content}</div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      <style jsx global>{`
        @keyframes lvge-vertical-bounce {
          0% {
            transform: scaleX(1) scaleY(1);
          }
          24% {
            transform: scaleX(calc(1 - 0.04 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 + 0.12 * var(--lp-squish-intensity, 1.55)));
          }
          52% {
            transform: scaleX(calc(1 + 0.025 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 - 0.05 * var(--lp-squish-intensity, 1.55)));
          }
          76% {
            transform: scaleX(calc(1 - 0.012 * var(--lp-squish-intensity, 1.55)))
              scaleY(calc(1 + 0.025 * var(--lp-squish-intensity, 1.55)));
          }
          100% {
            transform: scaleX(1) scaleY(1);
          }
        }

        /* Modo "cada chat es una LiquidPill". La LiquidPill envuelve
           header + contenido expandido, por eso mantiene los hover/reactive
           propios del composite y crece junto con el chat abierto. */
        .lvge-cell--individual-pill {
          position: relative;
          border-radius: 9999px;
          /* La esquina del cell anima en sincronía con el resto: pasa de
             píldora (9999px) a rounded-rect (22px) suavemente cuando se
             activa, en lugar de cambiar de golpe. */
          transition:
            border-radius 420ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .lvge-cell--individual-pill.lvge-cell--active.lvge-cell--has-content {
          border-radius: 22px;
        }
        .lvge-cell-pill {
          width: 100% !important;
          max-width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: stretch !important;
          justify-content: flex-start !important;
          gap: 0 !important;
          padding: 0 !important;
          border-radius: inherit !important;
        }
        .lvge-cell--individual-pill:not(.lvge-cell--active) .lvge-cell-pill {
          height: 56px !important;
          min-height: 56px !important;
          max-height: 56px !important;
        }

        /* Item — colores idle/active idénticos al LiquidPillGroupVertical */
        .lvge-item--idle {
          color: rgb(71 85 105);
        }
        .dark .lvge-item--idle {
          color: rgb(212 212 216);
        }
        .lvge-item--individual-pill {
          height: 100%;
          min-height: 56px;
          align-items: center;
          overflow: visible !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        .lvge-item--individual-pill > span:first-child {
          height: 100%;
          align-items: center;
        }
        .lvge-item--active {
          color: var(--gb-accent-color);
        }
        .dark .lvge-item--active {
          color: var(--gb-accent-color-dark);
        }
        .lvge-item:not(:disabled):active {
          transform: scale(0.985);
        }
        .lvge-item--idle:not(:disabled):hover {
          color: var(--gb-accent-color);
        }
        .dark .lvge-item--idle:not(:disabled):hover {
          color: var(--gb-accent-color-dark);
        }

        .lvge-item--idle img,
        .lvge-item--idle svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.85;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .lvge-item--idle img,
        .lvge-item--idle svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.7;
        }
        .lvge-item--idle:not(:disabled):hover img,
        .lvge-item--idle:not(:disabled):hover svg {
          opacity: 0.95;
        }
        .dark .lvge-item--idle:not(:disabled):hover img,
        .dark .lvge-item--idle:not(:disabled):hover svg {
          opacity: 0.92;
        }
        .lvge-item--active img,
        .lvge-item--active svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.95;
          transition: filter 280ms ease, opacity 280ms ease;
        }
        .dark .lvge-item--active img,
        .dark .lvge-item--active svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.92;
        }

        /* Celdas (wrapper de cada item + contenido expandible) */
        .lvge-cell {
          --lvge-cell-max: 96px;
          --lvge-cell-active-max: 760px;
          --lvge-cell-content-max: 620px;
          display: flex;
          flex-direction: column;
          width: 100%;
          min-width: 0;
          /* overflow: visible — antes era hidden, pero como ya no hay
             transición de max-height, no hace falta clipar nada. Además,
             el pill interno ya tiene su propio border-radius: inherit, así
             que las esquinas redondeadas se respetan sin necesidad de clip.
             Tener overflow:hidden acá recortaba los efectos laterales del
             LiquidSurface (border insets, cursor-glow, hover-deform) y se
             veían recortados al ensanchar el panel. */
          overflow: visible;
          max-height: var(--lvge-cell-max);
        }
        /* ── Choreography de apertura ───────────────────────────────────
           Sustituimos el gap del flex container por margin-bottom
           animable en cada cell, para que el espacio entre items
           también colapse cuando un cell se oculta. Así, al activar un
           chat:
             1. Los demás cells colapsan (max-height + margin + opacity +
                transform + blur) con stagger por índice — una onda desde
                arriba hacia abajo.
             2. El cell activo "sube" naturalmente a su nueva posición
                porque el layout reflowea continuamente mientras los
                vecinos colapsan (sin delay, transiciones aplicadas en
                paralelo).
             3. El border-radius del cell activo pasa de píldora a
                rounded-rect en sincronía (420ms).
             4. El contenido emerge dentro (animación del LiquidPill-
                Expansible, con delay para que arranque cuando los demás
                ya empezaron a desvanecerse). */
        .lvge-container {
          /* Anulamos el gap de Tailwind: ahora el espaciado lo da el
             margin-bottom de cada cell para poder animarlo. */
          gap: 0 !important;
        }
        .lvge-cell {
          margin-bottom: 4px;
          transition:
            max-height 420ms cubic-bezier(0.22, 1, 0.36, 1),
            margin-bottom 420ms cubic-bezier(0.22, 1, 0.36, 1),
            opacity 220ms ease,
            transform 420ms cubic-bezier(0.22, 1.02, 0.36, 1),
            filter 240ms ease;
          transition-delay: calc(var(--lvge-cell-index, 0) * 22ms);
        }
        .lvge-cell:last-child {
          margin-bottom: 0;
        }
        /* El cell activo no acumula stagger: empieza a moverse y a crecer
           desde el primer frame para que la sensación de respuesta sea
           inmediata al click. */
        .lvge-cell--active {
          transition-delay: 0ms !important;
        }
        .lvge-cell--hidden {
          /* Antes: display:none (snap inmediato). Ahora: colapso suave
             que permite que el activo se reubique con el flow del flex. */
          max-height: 0 !important;
          margin-bottom: 0 !important;
          opacity: 0;
          transform: scale(0.94) translateY(-6px);
          filter: blur(4px);
          pointer-events: none;
          overflow: hidden;
        }
        .lvge-cell--active.lvge-cell--has-content {
          max-height: var(--lvge-cell-active-max);
        }

        /* Contenido expandible */
        .lvge-cell__content {
          position: relative;
          z-index: 2;
          max-height: 0;
          overflow: hidden;
        }
        .lvge-cell--active .lvge-cell__content {
          max-height: var(--lvge-cell-content-max);
        }
        .lvge-cell__content-inner {
          padding-top: 8px;
        }
      `}</style>
    </div>
  );
}
